import { gql, useQuery } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Input from '@orn/shared-web/components/Input';
import { PhoneNumberInput, usePhoneNumberInput } from '@orn/shared-web/components/Input';
import { DollarIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import Entity from '../../components/entity';
import PhoneVerificationModal from '../../components/PhoneVerificationModal';
import { useUpdateUser } from '../../hooks/mutations/useUpdateUser';
import { useUpdateUserSettings } from '../../hooks/mutations/useUpdateUserSettings';
import logger from '../../lib/logger';

import type { GetUserSettingsQuery, GetUserSettingsQueryVariables } from './__generated__/SettingsPane.gql';

const GET_USER_SETTINGS = gql`
  query GetUserSettings {
    getUser {
      id
      phoneNumber
      phoneNumberVerifiedAt
      settings {
        lowBalanceThreshold
      }
    }
  }
`;

const formatThreshold = (value: number | null | undefined): string => {
  return value != null ? value.toFixed(2) : '';
};

interface ISectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: ISectionProps) => (
  <div className="flex flex-col gap-6">
    <div className="text-primary text-lg font-semibold">{title}</div>
    {children}
  </div>
);

interface IFieldProps {
  title: string;
  subtext: string;
  children: React.ReactNode;
}

const Field = ({ title, subtext, children }: IFieldProps) => (
  <div className="flex flex-col gap-2">
    <div className="flex flex-col gap-0.5">
      <div className="text-primary text-sm font-medium">{title}</div>
      <div className="text-tertiary text-sm">{subtext}</div>
    </div>
    {children}
  </div>
);

const SettingsPane = () => {
  const { data, loading: queryLoading } = useQuery<GetUserSettingsQuery, GetUserSettingsQueryVariables>(
    GET_USER_SETTINGS
  );

  const [updateUser, { loading: updateUserLoading }] = useUpdateUser();
  const [updateUserSettings, { loading: updateSettingsLoading }] = useUpdateUserSettings();

  // Phone number state managed by hook
  const {
    value: phoneNumber,
    validation: phoneValidation,
    handleChange: handlePhoneNumberChange,
    setHasTouched: setPhoneNumberTouched,
  } = usePhoneNumberInput(data?.getUser?.phoneNumber ?? '', true);

  const [showVerificationModal, setShowVerificationModal] = React.useState(false);

  const [lowBalanceThreshold, setLowBalanceThreshold] = React.useState('');
  const [thresholdTouched, setThresholdTouched] = React.useState(false);

  // Initialize threshold when data loads
  React.useEffect(() => {
    if (data?.getUser) {
      setLowBalanceThreshold(formatThreshold(data.getUser.settings.lowBalanceThreshold));
    }
  }, [data]);

  // Low balance threshold validation
  const thresholdValidation = React.useMemo(() => {
    if (!lowBalanceThreshold) {
      if (thresholdTouched) {
        return { isValid: false, errorMessage: 'Low balance threshold is required' };
      }
      return { isValid: false, errorMessage: '' };
    }

    const numValue = parseFloat(lowBalanceThreshold);
    if (isNaN(numValue)) {
      return { isValid: false, errorMessage: 'Please enter a valid number' };
    }

    if (numValue < 0) {
      return { isValid: false, errorMessage: 'Threshold must be a positive number' };
    }

    return { isValid: true, errorMessage: '' };
  }, [lowBalanceThreshold, thresholdTouched]);

  const handleThresholdChange = React.useCallback((value: string) => {
    setThresholdTouched(true);
    setLowBalanceThreshold(value);
  }, []);

  const handleThresholdBlur = React.useCallback(() => {
    if (lowBalanceThreshold) {
      const numValue = parseFloat(lowBalanceThreshold);
      if (!isNaN(numValue)) {
        setLowBalanceThreshold(formatThreshold(numValue));
      }
    }
  }, [lowBalanceThreshold]);

  const handleSave = async () => {
    // Mark all fields as touched to show validation errors
    setPhoneNumberTouched(true);
    setThresholdTouched(true);

    // Don't save if validation fails
    if (!phoneValidation.isValid || !thresholdValidation.isValid) {
      return;
    }

    const promises: Promise<unknown>[] = [];

    // Update phone number if changed
    const originalPhoneNumber = data?.getUser?.phoneNumber ?? '';
    const e164PhoneNumber = phoneValidation.e164Value || phoneNumber;
    if (e164PhoneNumber !== originalPhoneNumber) {
      promises.push(
        updateUser({
          variables: {
            input: {
              phoneNumber: e164PhoneNumber,
            },
          },
        })
      );
    }

    // Update low balance threshold if changed
    // Compare numeric values to handle precision issues (e.g., server has 100.004, user enters 100)
    const thresholdValue = parseFloat(lowBalanceThreshold);
    const originalThreshold = data?.getUser?.settings.lowBalanceThreshold;
    if (!isNaN(thresholdValue) && thresholdValue !== originalThreshold) {
      promises.push(
        updateUserSettings({
          variables: {
            input: {
              lowBalanceThreshold: thresholdValue,
            },
          },
        })
      );
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      logger.error('Failed to save settings:', error);
    }
  };

  const isSaving = updateUserLoading || updateSettingsLoading;

  const hasChanges = React.useMemo(() => {
    if (!data?.getUser) {
      return false;
    }
    const originalPhoneNumber = data.getUser.phoneNumber ?? '';
    const e164PhoneNumber = phoneValidation.e164Value || phoneNumber;

    // Compare numeric values for threshold to handle precision issues
    // (e.g., server stores 100.004, user enters 100 - both format to "100.00" but are different)
    const userThreshold = parseFloat(lowBalanceThreshold);
    const originalThreshold = data.getUser.settings.lowBalanceThreshold;
    const thresholdChanged = !isNaN(userThreshold) && userThreshold !== originalThreshold;

    return e164PhoneNumber !== originalPhoneNumber || thresholdChanged;
  }, [data, phoneNumber, lowBalanceThreshold, phoneValidation.e164Value]);

  const phoneChanged = React.useMemo(() => {
    if (!data?.getUser) {
      return false;
    }
    const originalPhoneNumber = data.getUser.phoneNumber ?? '';
    const e164PhoneNumber = phoneValidation.e164Value || phoneNumber;
    return e164PhoneNumber !== originalPhoneNumber;
  }, [data, phoneNumber, phoneValidation.e164Value]);

  const isFormValid = phoneValidation.isValid && thresholdValidation.isValid;

  if (queryLoading) {
    return (
      <Entity.Pane>
        <Entity.Header title="Settings" />
        <Entity.Body primaryElement={<div className="text-content-secondary">Loading...</div>} />
      </Entity.Pane>
    );
  }

  return (
    <>
      <Entity.Pane>
        <Entity.Header title="Settings" />
        <Entity.Body
          primaryElement={
            <div className="flex flex-col gap-10">
              <Section title="Profile">
                <Field title="Phone number" subtext="Your phone number for account notifications and recovery.">
                  <PhoneNumberInput
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    label=""
                    isRequired
                    isDisabled={isSaving}
                    className="max-w-sm"
                    validation={phoneValidation}
                  />
                  {phoneChanged && phoneValidation.isValid && (
                    <span className="text-warning-primary text-xs font-medium">
                      You'll need to re-verify your phone number after saving
                    </span>
                  )}
                  {!phoneChanged && data?.getUser?.phoneNumber && !data.getUser.phoneNumberVerifiedAt && (
                    <div className="flex items-center gap-2">
                      <span className="text-warning-primary text-xs font-medium">Unverified</span>
                      <Button color="primary" size="xs" onClick={() => setShowVerificationModal(true)}>
                        Verify phone
                      </Button>
                    </div>
                  )}
                  {!phoneChanged && data?.getUser?.phoneNumberVerifiedAt && (
                    <span className="text-success-primary text-xs font-medium">Verified</span>
                  )}
                </Field>
              </Section>

              <Section title="Alerts">
                <Field
                  title="Low balance threshold"
                  subtext="Get notified when any account balance falls below this amount."
                >
                  <Input
                    type="number"
                    placeholder="500.00"
                    icon={DollarIcon}
                    value={lowBalanceThreshold}
                    onChange={handleThresholdChange}
                    onBlur={handleThresholdBlur}
                    isInvalid={!!thresholdValidation.errorMessage}
                    isRequired
                    isDisabled={isSaving}
                    hideRequiredIndicator
                    hint={thresholdValidation.errorMessage || undefined}
                    size="md"
                    className="max-w-sm"
                  />
                </Field>
              </Section>

              <div className="flex justify-start pt-4">
                <Button
                  color="primary"
                  size="md"
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={!hasChanges || !isFormValid || isSaving}
                >
                  Save changes
                </Button>
              </div>
            </div>
          }
        />
      </Entity.Pane>
      {data?.getUser?.phoneNumber && (
        <PhoneVerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onVerified={() => setShowVerificationModal(false)}
          phoneNumber={data.getUser.phoneNumber}
        />
      )}
    </>
  );
};

export default SettingsPane;
